import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type ReactNode,
} from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  Link,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HubIcon from '@mui/icons-material/Hub';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import RuleIcon from '@mui/icons-material/Rule';
import SearchIcon from '@mui/icons-material/Search';
import SummarizeIcon from '@mui/icons-material/Summarize';
import PageHeader from '../../components/PageHeader';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useToast } from '../../components/Toast';
import { EmptyState, PageLoadingSpinner } from '../../components/ui';
import { useDocumentTitle } from '../../hooks';
import api, { ApiError } from '../../lib/api';
import OrgInputTextField from '../../components/OrgInputTextField';
import {
  bulkEnableGraphMonitoring,
  enableGraphMonitoring,
  fetchGraphMonitoringSettings,
  fetchGraphMonitoringRules,
  getAllGraphMonitoringRuleKeys,
  saveGraphMonitoringSettings,
  unsubscribeGraphMonitoring,
} from './monitoringApi';
import {
  GraphMonitoringEntitySummary,
  GraphMonitoringRuleCatalogEntry,
} from './types';
import {
  GraphMonitoringUploadParseResult,
  parseGraphMonitoringUploadFile,
} from './uploadUtils';
import { formatDateTime, formatRelativeTime, getEntityDisplayName } from './utils';
import { useTranslation } from 'react-i18next';
import appI18n from '../../i18n/i18n';
import { compareDisplayText } from '../../i18n/formatters';
import {
  getMonitoringRuleDescription,
  getMonitoringRuleName,
} from '../../i18n/backendTranslations';

interface ActiveMonitoredEntity extends GraphMonitoringEntitySummary {}
interface UploadProgressState {
  current: number;
  total: number;
  indeterminate?: boolean;
}

function OverviewMetricCard({
  icon,
  label,
  value,
  helper,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  helper: string;
}) {
  return (
    <Paper
      sx={{
        p: 2.5,
        borderRadius: 'var(--radius-xl)',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            display: 'grid',
            placeItems: 'center',
            borderRadius: 'var(--radius-lg)',
            bgcolor: 'action.hover',
            color: 'primary.main',
          }}
        >
          {icon}
        </Box>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Box>
      <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {helper}
      </Typography>
    </Paper>
  );
}

function getErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (typeof error.body === 'string' && error.body.trim()) {
      return error.body;
    }

    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return appI18n.t('monitoring.overview.loadFailed');
}

function isBulkEndpointUnavailable(error: unknown) {
  return error instanceof ApiError && [404, 405, 501].includes(error.status);
}

function sortRuleKeys(ruleKeys: string[]) {
  return [...ruleKeys].sort((left, right) => compareDisplayText(left, right, appI18n.resolvedLanguage));
}

function areRuleKeySetsEqual(left: string[], right: string[]) {
  return JSON.stringify(sortRuleKeys(left)) === JSON.stringify(sortRuleKeys(right));
}

export default function GraphMonitoringOverview() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const [entities, setEntities] = useState<ActiveMonitoredEntity[]>([]);
  const [availableRules, setAvailableRules] = useState<GraphMonitoringRuleCatalogEntry[]>([]);
  const [selectedRuleKeys, setSelectedRuleKeys] = useState<string[]>([]);
  const [savedRuleKeys, setSavedRuleKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [savingRules, setSavingRules] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rulesError, setRulesError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrgNumber, setSelectedOrgNumber] = useState('');
  const [selectedDisplayName, setSelectedDisplayName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<UploadProgressState | null>(
    null,
  );
  const [parsedUpload, setParsedUpload] = useState<GraphMonitoringUploadParseResult | null>(null);
  const [uploadSummary, setUploadSummary] = useState<{
    successCount: number;
    failures: Array<{ orgNumber: string; message: string }>;
  } | null>(null);
  const [removingOrgNumber, setRemovingOrgNumber] = useState<string | null>(null);
  const [lastLoadedAt, setLastLoadedAt] = useState<Date | null>(null);
  const deferredSearchTerm = useDeferredValue(searchTerm.trim().toLowerCase());
  const localizedAvailableRules = useMemo(
    () => [...availableRules].sort((left, right) => compareDisplayText(
      getMonitoringRuleName(left.ruleKey, left.displayName),
      getMonitoringRuleName(right.ruleKey, right.displayName),
      i18n.resolvedLanguage,
    )),
    [availableRules, i18n.resolvedLanguage],
  );

  useDocumentTitle(`${t('monitoring.overviewTitle')} — Breadify`);

  const fetchOverview = useCallback(async (mode: 'initial' | 'refresh' = 'initial') => {
    if (mode === 'initial') {
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    setError(null);
    setRulesError(null);

    try {
      const [entitiesResult, rulesResult, settingsResult] = await Promise.allSettled([
        api.get<GraphMonitoringEntitySummary[]>('/v1/graphmonitoring'),
        fetchGraphMonitoringRules(),
        fetchGraphMonitoringSettings(),
      ]);

      if (rulesResult.status === 'fulfilled') {
        setAvailableRules(rulesResult.value);
      } else {
        setAvailableRules([]);
        setRulesError(getErrorMessage(rulesResult.reason));
      }

      if (settingsResult.status === 'fulfilled') {
        const sortedRuleKeys = sortRuleKeys(settingsResult.value.ruleKeys);
        setSelectedRuleKeys(sortedRuleKeys);
        setSavedRuleKeys(sortedRuleKeys);
      } else {
        setSelectedRuleKeys([]);
        setSavedRuleKeys([]);
        setRulesError((currentError) => {
          const nextMessage = getErrorMessage(settingsResult.reason);
          return currentError ? `${currentError} ${nextMessage}` : nextMessage;
        });
      }

      if (entitiesResult.status === 'fulfilled') {
        const activeEntities = entitiesResult.value
          .slice()
          .sort((left, right) =>
            compareDisplayText(getEntityDisplayName(left), getEntityDisplayName(right), appI18n.resolvedLanguage),
          );
        setEntities(activeEntities);
        setLastLoadedAt(new Date());
      } else {
        setError(getErrorMessage(entitiesResult.reason));
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const filteredEntities = useMemo(() => {
    if (!deferredSearchTerm) {
      return entities;
    }

    return entities.filter((entity) =>
      [getEntityDisplayName(entity), entity.externalId]
        .join(' ')
        .toLowerCase()
        .includes(deferredSearchTerm),
    );
  }, [deferredSearchTerm, entities]);

  const selectedRuleKeySet = useMemo(
    () => new Set(selectedRuleKeys),
    [selectedRuleKeys],
  );

  const hasUnsavedRuleChanges = useMemo(
    () => !areRuleKeySetsEqual(selectedRuleKeys, savedRuleKeys),
    [savedRuleKeys, selectedRuleKeys],
  );

  const handleToggleRule = useCallback((ruleKey: string) => {
    setSelectedRuleKeys((currentRuleKeys) => {
      const nextRuleKeys = new Set(currentRuleKeys);

      if (nextRuleKeys.has(ruleKey)) {
        nextRuleKeys.delete(ruleKey);
      } else {
        nextRuleKeys.add(ruleKey);
      }

      return sortRuleKeys(Array.from(nextRuleKeys));
    });
  }, []);

  const handleSelectAllRules = useCallback(() => {
    setSelectedRuleKeys(getAllGraphMonitoringRuleKeys(availableRules));
  }, [availableRules]);

  const handleClearRules = useCallback(() => {
    setSelectedRuleKeys([]);
  }, []);

  const handleSaveRules = useCallback(async () => {
    setSavingRules(true);
    setRulesError(null);

    try {
      const settings = await saveGraphMonitoringSettings(selectedRuleKeys);
      const sortedRuleKeys = sortRuleKeys(settings.ruleKeys);
      setSelectedRuleKeys(sortedRuleKeys);
      setSavedRuleKeys(sortedRuleKeys);
      toast.success(t('monitoring.overview.rulesUpdated'));
    } catch (saveError) {
      const message = getErrorMessage(saveError);
      setRulesError(message);
      toast.error(message);
    } finally {
      setSavingRules(false);
    }
  }, [selectedRuleKeys, t, toast]);

  const handleOpenCompany = () => {
    if (!selectedOrgNumber) {
      return;
    }

    navigate({
      pathname: `/graphmonitoring/${selectedOrgNumber}`,
      search: selectedDisplayName
        ? `?displayName=${encodeURIComponent(selectedDisplayName)}`
        : '',
    });
  };

  const handleUploadFileSelected = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';

      if (!file) {
        return;
      }

      setUploadError(null);
      setUploadSummary(null);

      try {
        const parsed = await parseGraphMonitoringUploadFile(file);
        setParsedUpload(parsed);

        if (!parsed.orgNumbers.length) {
          setUploadError(t('monitoring.overview.noValidOrgNumbers'));
        }
      } catch (parseError) {
        setParsedUpload(null);
        setUploadError(getErrorMessage(parseError));
      }
    },
    [t],
  );

  const runLegacyBulkMonitor = useCallback(async (orgNumbers: string[]) => {
    const failures: Array<{ orgNumber: string; message: string }> = [];
    let successCount = 0;
    let completed = 0;
    const chunks: string[][] = [];
    const concurrency = 5;

    for (let index = 0; index < orgNumbers.length; index += concurrency) {
      chunks.push(orgNumbers.slice(index, index + concurrency));
    }

    const processChunk = async (chunk: string[]) => {
      await Promise.all(
        chunk.map(async (orgNumber) => {
          try {
            await enableGraphMonitoring(orgNumber);
            successCount += 1;
          } catch (uploadRequestError) {
            failures.push({
              orgNumber,
              message: getErrorMessage(uploadRequestError),
            });
          } finally {
            completed += 1;
            setUploadProgress({ current: completed, total: orgNumbers.length });
          }
        }),
      );
    };

    for (const chunk of chunks) {
      await processChunk(chunk);
    }

    return { successCount, failures };
  }, []);

  const handleBulkMonitor = useCallback(async () => {
    if (!parsedUpload?.orgNumbers.length) {
      return;
    }

    setUploading(true);
    setUploadError(null);
    setUploadSummary(null);
    setUploadProgress({
      current: 0,
      total: parsedUpload.orgNumbers.length,
      indeterminate: true,
    });

    try {
      let result: {
        successCount: number;
        failures: Array<{ orgNumber: string; message: string }>;
      };

      try {
        const response = await bulkEnableGraphMonitoring(parsedUpload.orgNumbers);
        result = {
          successCount: response.successes.length,
          failures: response.failures.map((failure) => ({
            orgNumber: failure.orgNumber,
            message: failure.message,
          })),
        };
      } catch (uploadRequestError) {
        if (!isBulkEndpointUnavailable(uploadRequestError)) {
          throw uploadRequestError;
        }

        setUploadProgress({ current: 0, total: parsedUpload.orgNumbers.length });
        result = await runLegacyBulkMonitor(parsedUpload.orgNumbers);
      }

      setUploadSummary(result);

      if (result.successCount > 0) {
        await fetchOverview('refresh');
      }

      if (result.failures.length === 0) {
        toast.success(t('monitoring.overview.bulkEnabled', { count: result.successCount }));
      } else if (result.successCount > 0) {
        toast.warning(
          t('monitoring.overview.bulkPartlyEnabled', { count: result.successCount, failed: result.failures.length }),
        );
      } else {
        toast.error(t('monitoring.overview.noneAdded'));
      }
    } catch (uploadRequestError) {
      const message = getErrorMessage(uploadRequestError);
      setUploadError(message);
      toast.error(message);
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  }, [fetchOverview, parsedUpload, runLegacyBulkMonitor, t, toast]);

  const handleStopMonitoring = useCallback(
    async (entity: ActiveMonitoredEntity) => {
      setRemovingOrgNumber(entity.externalId);

      try {
        await unsubscribeGraphMonitoring(entity.externalId);
        setEntities((previousEntities) =>
          previousEntities.filter((currentEntity) => currentEntity.externalId !== entity.externalId),
        );
        toast.success(t('monitoring.overview.removedFromMonitoring', { name: getEntityDisplayName(entity) }));
      } catch (stopError) {
        toast.error(getErrorMessage(stopError));
      } finally {
        setRemovingOrgNumber(null);
      }
    },
    [t, toast],
  );

  if (loading) {
    return (
      <Box sx={{ width: '100%', minHeight: '60vh', display: 'grid', placeItems: 'center' }}>
        <PageLoadingSpinner label={t('monitoring.overview.loading')} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3, '& > :first-child': { mb: 0 } }}>
      <PageHeader title={t('monitoring.overviewTitle')} icon={<HubIcon />} description={<>
          {t('monitoring.overview.description')}
          {lastLoadedAt && (
            <Typography variant="caption" component="span" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {t('monitoring.updatedAt', { time: lastLoadedAt.toLocaleTimeString(i18n.resolvedLanguage === 'sv' ? 'sv-SE' : 'en-SE', {
                hour: '2-digit',
                minute: '2-digit',
              }) })}
            </Typography>
          )}
        </>} actions={<>
        <Button
          component={RouterLink}
          to="/graphmonitoring/portfolio-diff"
          variant="outlined"
          startIcon={<SummarizeIcon />}
        >
          {t('monitoring.overview.portfolioSummary')}
        </Button>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          disabled={refreshing}
          onClick={() => fetchOverview('refresh')}
        >
          {refreshing ? t('monitoring.overview.refreshing') : t('common.actions.refresh')}
        </Button>
      </>} />

      <Box
        sx={{
          maxWidth: 360,
        }}
      >
        <OverviewMetricCard
          icon={<HubIcon />}
          label={t('monitoring.overview.companiesMonitored')}
          value={String(entities.length)}
          helper={t('monitoring.overview.companiesMonitoredHelp')}
        />
      </Box>

      <Paper
        sx={{
          p: 2.5,
          borderRadius: 'var(--radius-xl)',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) auto' },
            gap: 2,
            alignItems: 'end',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {t('monitoring.overview.openOrAdd')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('monitoring.overview.openOrAddHelp')}
            </Typography>
            <OrgInputTextField
              disabled={false}
              textFieldProps={{
                label: t('monitoring.overview.companyNameOrNumber'),
                sx: {
                  width: '100%',
                },
              }}
              onValidOrgNumberEntered={(orgNumber) => setSelectedOrgNumber(orgNumber)}
              onInvalidOrgNumberEntered={() => {
                setSelectedOrgNumber('');
                setSelectedDisplayName('');
              }}
              onOptionSelected={(option) => {
                setSelectedDisplayName(option?.label ?? '');
              }}
            />
          </Box>
          <Button
            variant="contained"
            startIcon={<OpenInNewIcon />}
            onClick={handleOpenCompany}
            disabled={!selectedOrgNumber}
            sx={{ minWidth: 180, height: 'fit-content' }}
          >
            {t('monitoring.overview.openCompany')}
          </Button>
        </Box>
      </Paper>

      <Paper
        sx={{
          p: 2.5,
          borderRadius: 'var(--radius-xl)',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {t('monitoring.overview.bulkAdd')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 760 }}>
              {t('monitoring.overview.bulkAddHelp')}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button
              component="label"
              variant="outlined"
              startIcon={<UploadFileIcon />}
              disabled={uploading}
            >
              {t('monitoring.overview.chooseFile')}
              <input
                hidden
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleUploadFileSelected}
              />
            </Button>
            <Button
              variant="contained"
              onClick={handleBulkMonitor}
              disabled={uploading || !parsedUpload?.orgNumbers.length}
            >
              {uploading ? t('monitoring.overview.addingCompanies') : t('monitoring.overview.monitorFile')}
            </Button>
            {parsedUpload && (
              <Typography variant="body2" color="text.secondary">
                {parsedUpload.fileName}
              </Typography>
            )}
          </Box>

          {parsedUpload && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={t('monitoring.overview.validOrgNumbers', { count: parsedUpload.orgNumbers.length })}
                  color="primary"
                  size="small"
                />
                {parsedUpload.duplicateCount > 0 && (
                  <Chip
                    label={t('monitoring.overview.duplicatesRemoved', { count: parsedUpload.duplicateCount })}
                    size="small"
                    variant="outlined"
                  />
                )}
                {parsedUpload.invalidRowCount > 0 && (
                  <Chip
                    label={t('monitoring.overview.rowsSkipped', { count: parsedUpload.invalidRowCount })}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>
              {parsedUpload.previewOrgNumbers.length > 0 && (
                <Typography variant="body2" color="text.secondary">
                  {t('monitoring.overview.preview')}: {parsedUpload.previewOrgNumbers.join(', ')}
                  {parsedUpload.orgNumbers.length > parsedUpload.previewOrgNumbers.length
                    ? '...'
                    : ''}
                </Typography>
              )}
            </Box>
          )}

          {uploadProgress && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <LinearProgress
                variant={uploadProgress.indeterminate ? 'indeterminate' : 'determinate'}
                value={
                  uploadProgress.indeterminate
                    ? undefined
                    : (uploadProgress.current / uploadProgress.total) * 100
                }
              />
              <Typography variant="body2" color="text.secondary">
                {uploadProgress.indeterminate
                  ? t('monitoring.overview.addingCount', { count: uploadProgress.total })
                  : t('monitoring.overview.processedCount', { current: uploadProgress.current, total: uploadProgress.total })}
              </Typography>
            </Box>
          )}

          {uploadError && <Alert severity="error">{uploadError}</Alert>}

          {uploadSummary && (
            <Alert severity={uploadSummary.failures.length ? 'warning' : 'success'}>
              {t('monitoring.overview.bulkEnabled', { count: uploadSummary.successCount })}
              {uploadSummary.failures.length
                ? ` ${t('monitoring.overview.bulkFailures', { count: uploadSummary.failures.length })}: ${uploadSummary.failures
                    .slice(0, 5)
                    .map((failure) => `${failure.orgNumber} (${failure.message})`)
                    .join(', ')}${uploadSummary.failures.length > 5 ? '...' : ''}`
                : ''}
            </Alert>
          )}
        </Box>
      </Paper>

      <Accordion
        disableGutters
        sx={{
          borderRadius: 'var(--radius-xl)',
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontWeight: 700 }}>{t('monitoring.settingsTitle')}</Typography>
            <Chip label={t('monitoring.overview.selectedCount', { count: selectedRuleKeys.length })} size="small" variant="outlined" />
            <Chip label={t('monitoring.overview.availableCount', { count: availableRules.length })} size="small" variant="outlined" />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              {t('monitoring.overview.rulesHelp')}
            </Typography>

            {rulesError && <Alert severity="warning">{rulesError}</Alert>}

            {!availableRules.length ? (
              <EmptyState
                icon={<RuleIcon />}
                title={t('monitoring.overview.noRulesAvailable')}
                description={t('monitoring.overview.noRulesAvailableHelp')}
                sx={{ py: 6 }}
              />
            ) : (
              <>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button variant="outlined" onClick={handleSelectAllRules} disabled={savingRules}>
                    {t('common.actions.selectAll')}
                  </Button>
                  <Button variant="outlined" onClick={handleClearRules} disabled={savingRules}>
                    {t('monitoring.overview.clearAll')}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSaveRules}
                    disabled={savingRules || !availableRules.length || !hasUnsavedRuleChanges}
                  >
                    {savingRules ? t('monitoring.overview.saving') : t('monitoring.overview.saveRules')}
                  </Button>
                </Box>

                {hasUnsavedRuleChanges && (
                  <Alert severity="info">
                    {t('monitoring.overview.unsavedRules')}
                  </Alert>
                )}

                {localizedAvailableRules.map((rule) => (
                  <Paper
                    key={rule.ruleKey}
                    variant="outlined"
                    sx={{ p: 2, borderRadius: 'var(--radius-lg)' }}
                  >
                    <FormControlLabel
                      sx={{ alignItems: 'flex-start', m: 0, width: '100%' }}
                      control={
                        <Checkbox
                          checked={selectedRuleKeySet.has(rule.ruleKey)}
                          onChange={() => handleToggleRule(rule.ruleKey)}
                          disabled={savingRules}
                        />
                      }
                      label={
                        <Box sx={{ pt: 0.5 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                            {getMonitoringRuleName(rule.ruleKey, rule.displayName)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {getMonitoringRuleDescription(rule.ruleKey, rule.description)}
                          </Typography>
                        </Box>
                      }
                    />
                  </Paper>
                ))}
              </>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {error && <Alert severity="error">{error}</Alert>}

      <Accordion
        disableGutters
        sx={{
          borderRadius: 'var(--radius-xl)',
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontWeight: 700 }}>{t('monitoring.overview.companiesMonitored')}</Typography>
            <Chip label={t('common.counts.company', { count: entities.length })} size="small" variant="outlined" />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              label={t('monitoring.overview.searchMonitored')}
              placeholder={t('monitoring.overview.companyNameOrNumber')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            {filteredEntities.length === 0 ? (
              <EmptyState
                icon={<HubIcon />}
                title={t(entities.length === 0 ? 'monitoring.overview.noCompanies' : 'monitoring.overview.noMatchingCompanies')}
                description={
                  entities.length === 0
                    ? t('monitoring.overview.noCompaniesHelp')
                    : t('monitoring.overview.noMatchingCompaniesHelp')
                }
                sx={{ py: 6 }}
              />
            ) : (
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                  borderRadius: 'var(--radius-lg)',
                  overflow: 'hidden',
                }}
              >
                <Table size="small" aria-label={t('monitoring.overview.activeCompanies')}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('monitoring.overview.company')}</TableCell>
                      <TableCell>{t('monitoring.overview.orgNumber')}</TableCell>
                      <TableCell>{t('monitoring.overview.monitoredSince')}</TableCell>
                      <TableCell align="right">{t('common.actions.remove')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredEntities.map((entity) => (
                      <TableRow key={entity.id} hover>
                        <TableCell sx={{ py: 1.25 }}>
                          <Link
                            component={RouterLink}
                            to={`/graphmonitoring/${entity.externalId}`}
                            underline="hover"
                            sx={{ fontWeight: 600 }}
                          >
                            {getEntityDisplayName(entity)}
                          </Link>
                        </TableCell>
                        <TableCell sx={{ py: 1.25 }}>{entity.externalId}</TableCell>
                        <TableCell sx={{ py: 1.25 }}>
                          <Typography variant="body2">
                            {formatDateTime(entity.createdAt)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatRelativeTime(entity.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1.25 }}>
                          <Button
                            size="small"
                            color="inherit"
                            variant="outlined"
                            startIcon={
                              removingOrgNumber === entity.externalId ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : (
                                <VisibilityOffIcon />
                              )
                            }
                            disabled={removingOrgNumber === entity.externalId}
                            onClick={() => handleStopMonitoring(entity)}
                          >
                            {removingOrgNumber === entity.externalId ? t('monitoring.overview.removing') : t('common.actions.remove')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
